/**
 * Unified AI Workforce Roster — same source of truth as AI Studio (`ai_agents`).
 * Code Reviewer is seeded as `ai_agents/code_reviewer_{companyId}` when missing.
 *
 * Model policy: Claude Sonnet 5 for agents by default; Gemini only for CSA;
 * `local:…` self-hosted targets are preserved as-is.
 */

export const CODE_REVIEWER_AGENT_ID = 'code_reviewer';
export const CUSTOMER_SUCCESS_AGENT_ID = 'CSA';

export const AI_ROSTER_AGENT_STATUSES = [
  { id: 'enabled', label: 'Enabled' },
  { id: 'disabled', label: 'Disabled' },
];

/**
 * Model targets stored on `ai_agents.modelTarget` (Claude + Gemini + DeepSeek + local).
 * Ids match runtime model names (no 1.5 display aliases).
 */
export const AI_ROSTER_MODEL_OPTIONS = [
  {
    id: 'claude-sonnet-5',
    label: 'claude-sonnet-5 (Anthropic Claude — default for agents / code)',
  },
  {
    id: 'gemini-2.5-flash',
    label: 'gemini-2.5-flash (Google Gemini — CSA default / fast)',
  },
  {
    id: 'gemini-3.6-flash',
    label: 'gemini-3.6-flash (Google Gemini — agentic / tool-loop quality)',
  },
  {
    id: 'gemini-3.5-flash-lite',
    label: 'gemini-3.5-flash-lite (Google Gemini — cheapest 3.x / high volume)',
  },
  {
    id: 'deepseek-v4-flash',
    label: 'deepseek-v4-flash (DeepSeek — fast / cost-efficient)',
  },
  {
    id: 'deepseek-v4-pro',
    label: 'deepseek-v4-pro (DeepSeek — higher quality)',
  },
  {
    id: 'local:default',
    label: 'local (OpenAI-compatible — company BYOK or platform fallback)',
  },
];

/** Default for Code Reviewer, engineers, and new hires. */
export const DEFAULT_MODEL_TARGET = 'claude-sonnet-5';

/** Default for Customer Success Agent (Gemini 3.6 Flash). */
export const CSA_MODEL_TARGET = 'gemini-3.6-flash';

/** Selectable Gemini tiers (must stay in sync with functions/lib/aiRoster.js). */
export const GEMINI_MODEL_TARGETS = [
  'gemini-2.5-flash',
  'gemini-3.6-flash',
  'gemini-3.5-flash-lite',
];

export const DEFAULT_CODE_REVIEWER_SYSTEM_PROMPT = `You are a Senior QA Engineer / Judge — an adversarial code auditor for an AI agent swarm.

Your mission is to hunt bugs before they reach production. Assume the patch author may hallucinate APIs, imports, props, and file paths.

## Primary hunt list
1. **Fatal runtime risks** — null/undefined dereferences, missing awaits, uncaught promise rejections, wrong arity, off-by-one loops.
2. **Security** — leaked secrets, unsafe innerHTML, missing auth checks, injection surfaces, permissive Firestore rules patterns.
3. **Architectural drift** — hallucinated modules, wrong package names, schema mismatches, breaking public contracts.
4. **React / Material-UI integration bugs** — especially:
   - \`FormControl\` + \`InputLabel\` + \`Select\` / \`TextField\` must use matching \`labelId\` / \`id\` and \`label\` props or labels fail to associate (accessibility + layout bugs).
   - Controlled vs uncontrolled input flips when \`value\` becomes \`undefined\`.
   - Missing \`key\` in lists, stale closures in hooks, incorrect \`useEffect\` dependency arrays.
5. **Regression risk** — does the patch solve the stated intent without breaking adjacent flows?

## Output format
Respond with:
- **Summary** (1–2 sentences)
- **Critical issues** (must-fix before merge)
- **Security concerns**
- **Architecture / maintainability notes**
- **Recommended next steps** (concrete, file-scoped)

Be precise. Cite file paths and line-level guidance when possible. Do not praise code unnecessarily.`;

export const DEFAULT_CUSTOMER_SUCCESS_SYSTEM_PROMPT =
  'You are the L1 Customer Success Agent for ShiftyPM HQ. Your job is to ' +
  'interact with external tenants who submit bug reports. Acknowledge their ' +
  'issue, ask clarifying questions (browser, steps to reproduce, screenshots), ' +
  'and apologize for the inconvenience. When you have enough context, update ' +
  "the ticket status to 'Awaiting HQ Verification'. YOU MUST NEVER ATTEMPT " +
  'TO WRITE CODE OR FIX THE SYSTEM DIRECTLY.';

/**
 * @typedef {object} AiRosterAgent
 * @property {string} id
 * @property {string} agentName
 * @property {string} roleTitle
 * @property {string} [department]
 * @property {string} modelTarget
 * @property {string} systemPrompt
 * @property {'enabled'|'disabled'} status
 * @property {boolean} [isCodeReviewer]
 * @property {boolean} [isCustomerSuccess]
 * @property {string[]} [allowedTools]
 */

/**
 * @param {string | null | undefined} raw
 * @returns {'enabled'|'disabled'}
 */
export function normalizeAgentStatus(raw) {
  const value = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (value === 'disabled' || value === 'inactive' || value === 'archived') {
    return 'disabled';
  }
  return 'enabled';
}

/**
 * Map UI status back to AI Studio `status` field.
 * @param {'enabled'|'disabled'|string} uiStatus
 * @returns {'active'|'inactive'}
 */
export function uiStatusToStudioStatus(uiStatus) {
  return uiStatus === 'disabled' ? 'inactive' : 'active';
}

/**
 * Normalize stored / legacy model ids for roster UI and seed payloads.
 * - `local:…` preserved
 * - Claude preserved
 * - DeepSeek (`deepseek-…`) preserved
 * - Current Gemini tiers (2.5 / 3.6 / 3.5-lite) preserved as selected
 * - Legacy Gemini 1.5 Pro (old agent default) → Claude
 * - Retired flash / mini → gemini-2.5-flash (CSA default)
 * @param {string | null | undefined} modelTarget
 * @returns {string}
 */
export function normalizeModelTarget(modelTarget) {
  const value = typeof modelTarget === 'string' ? modelTarget.trim() : '';
  if (!value) return DEFAULT_MODEL_TARGET;
  if (value.toLowerCase().startsWith('local:')) {
    return value;
  }
  if (value.startsWith('claude-')) {
    return value;
  }
  if (value === 'claude') {
    return 'claude-sonnet-5';
  }
  if (value.toLowerCase().startsWith('deepseek-')) {
    return value;
  }
  // Current selectable Gemini tiers — keep as pinned (do not collapse).
  if (GEMINI_MODEL_TARGETS.includes(value)) {
    return value;
  }
  // Retired flash / mini labels → CSA default (2.5 flash).
  if (value === 'gemini-1.5-flash' || value === 'gpt-4o-mini') {
    return CSA_MODEL_TARGET;
  }
  // Old engineer / reviewer default was gemini-1.5-pro — migrate to Claude.
  if (
    value === 'gemini-1.5-pro' ||
    value === 'gemini-2.5-pro' ||
    value === 'gpt-4o'
  ) {
    return DEFAULT_MODEL_TARGET;
  }
  const modelIds = new Set(AI_ROSTER_MODEL_OPTIONS.map((m) => m.id));
  if (modelIds.has(value)) return value;
  if (value.startsWith('gemini-')) return CSA_MODEL_TARGET;
  return DEFAULT_MODEL_TARGET;
}

/**
 * Normalize an `ai_agents` document (or legacy ai_roster shape) for the roster UI.
 * @param {string} agentId
 * @param {object | null | undefined} raw
 * @returns {AiRosterAgent}
 */
export function normalizeStudioAgent(agentId, raw) {
  const data = raw && typeof raw === 'object' ? raw : {};
  const isCodeReviewer =
    agentId === CODE_REVIEWER_AGENT_ID ||
    (typeof agentId === 'string' && agentId.startsWith('code_reviewer_')) ||
    data.rosterRole === CODE_REVIEWER_AGENT_ID ||
    data.isCodeReviewer === true;

  const isCustomerSuccess =
    agentId === CUSTOMER_SUCCESS_AGENT_ID ||
    (typeof agentId === 'string' &&
      (agentId === 'CSA' || agentId.startsWith('csa_'))) ||
    data.rosterRole === CUSTOMER_SUCCESS_AGENT_ID ||
    data.isCustomerSuccess === true;

  const agentName =
    (typeof data.name === 'string' && data.name.trim()) ||
    (typeof data.agentName === 'string' && data.agentName.trim()) ||
    (isCodeReviewer
      ? 'Senior QA Engineer / Judge'
      : isCustomerSuccess
        ? 'Customer Success Agent'
        : 'Untitled agent');

  const roleTitle =
    (typeof data.roleTitle === 'string' && data.roleTitle.trim()) ||
    (isCodeReviewer
      ? 'Senior QA Engineer / Judge'
      : isCustomerSuccess
        ? 'Customer Success Agent'
        : '');

  const systemPrompt =
    typeof data.systemPrompt === 'string' && data.systemPrompt.trim()
      ? data.systemPrompt
      : isCodeReviewer
        ? DEFAULT_CODE_REVIEWER_SYSTEM_PROMPT
        : isCustomerSuccess
          ? DEFAULT_CUSTOMER_SUCCESS_SYSTEM_PROMPT
          : '';

  const department =
    (typeof data.department === 'string' && data.department.trim()) ||
    (isCustomerSuccess ? 'Support' : '');

  return {
    id: agentId,
    agentName,
    roleTitle,
    department,
    modelTarget: normalizeModelTarget(
      data.modelTarget || (isCustomerSuccess ? CSA_MODEL_TARGET : DEFAULT_MODEL_TARGET),
    ),
    systemPrompt,
    status: normalizeAgentStatus(data.status),
    isCodeReviewer,
    isCustomerSuccess,
    allowedTools: Array.isArray(data.allowedTools) ? data.allowedTools : undefined,
  };
}

/**
 * Payload used when seeding the Code Reviewer into `ai_agents`.
 * @param {string} companyId
 * @param {string | null} uid
 * @param {string} [email]
 * @param {object} [overrides]
 * @returns {object}
 */
export function buildCodeReviewerSeedPayload(companyId, uid, email = '', overrides = {}) {
  return {
    companyId,
    name:
      (typeof overrides.agentName === 'string' && overrides.agentName.trim()) ||
      (typeof overrides.name === 'string' && overrides.name.trim()) ||
      'Senior QA Engineer / Judge',
    roleTitle: 'Senior QA Engineer / Judge',
    temperature: 0.15,
    systemPrompt:
      (typeof overrides.systemPrompt === 'string' && overrides.systemPrompt.trim()) ||
      DEFAULT_CODE_REVIEWER_SYSTEM_PROMPT,
    modelTarget:
      typeof overrides.modelTarget === 'string' && overrides.modelTarget.trim()
        ? normalizeModelTarget(overrides.modelTarget)
        : DEFAULT_MODEL_TARGET,
    default_boot_context: '',
    allowedTools: ['read_repo_tree'],
    contextMemoryLimit: 20,
    status: uiStatusToStudioStatus(overrides.status || 'enabled'),
    rosterRole: CODE_REVIEWER_AGENT_ID,
    isCodeReviewer: true,
    createdAt: overrides.createdAt,
    createdBy: uid || null,
    createdByEmail: email || '',
    updatedAt: overrides.updatedAt,
    updatedBy: uid || null,
    updatedByEmail: email || '',
  };
}

/**
 * Payload used when seeding the Customer Success Agent into `ai_agents`.
 * Intended for SHIFTYPM_HQ (cross-tenant support inbox).
 * @param {string} companyId
 * @param {string | null} uid
 * @param {string} [email]
 * @param {object} [overrides]
 * @returns {object}
 */
export function buildCustomerSuccessSeedPayload(companyId, uid, email = '', overrides = {}) {
  return {
    companyId,
    name:
      (typeof overrides.agentName === 'string' && overrides.agentName.trim()) ||
      (typeof overrides.name === 'string' && overrides.name.trim()) ||
      'Customer Success Agent',
    roleTitle: 'Customer Success Agent',
    department: 'Support',
    temperature: 0.4,
    systemPrompt:
      (typeof overrides.systemPrompt === 'string' && overrides.systemPrompt.trim()) ||
      DEFAULT_CUSTOMER_SUCCESS_SYSTEM_PROMPT,
    modelTarget:
      typeof overrides.modelTarget === 'string' && overrides.modelTarget.trim()
        ? normalizeModelTarget(overrides.modelTarget)
        : CSA_MODEL_TARGET,
    default_boot_context: '',
    allowedTools: ['update_ticket_status', 'reply_to_ticket'],
    contextMemoryLimit: 20,
    status: uiStatusToStudioStatus(overrides.status || 'enabled'),
    rosterRole: CUSTOMER_SUCCESS_AGENT_ID,
    isCustomerSuccess: true,
    createdAt: overrides.createdAt,
    createdBy: uid || null,
    createdByEmail: email || '',
    updatedAt: overrides.updatedAt,
    updatedBy: uid || null,
    updatedByEmail: email || '',
  };
}

/** @deprecated Prefer normalizeStudioAgent */
export function normalizeRosterAgent(agentId, raw) {
  return normalizeStudioAgent(agentId, raw);
}

export const DEFAULT_AI_ROSTER_AGENTS = [
  {
    id: CODE_REVIEWER_AGENT_ID,
    agentName: 'Senior QA Engineer / Judge',
    roleTitle: 'Senior QA Engineer / Judge',
    modelTarget: DEFAULT_MODEL_TARGET,
    systemPrompt: DEFAULT_CODE_REVIEWER_SYSTEM_PROMPT,
    status: 'enabled',
    isCodeReviewer: true,
    allowedTools: ['read_repo_tree'],
  },
  {
    id: CUSTOMER_SUCCESS_AGENT_ID,
    agentName: 'Customer Success Agent',
    roleTitle: 'Customer Success Agent',
    department: 'Support',
    modelTarget: CSA_MODEL_TARGET,
    systemPrompt: DEFAULT_CUSTOMER_SUCCESS_SYSTEM_PROMPT,
    status: 'enabled',
    isCustomerSuccess: true,
    allowedTools: ['update_ticket_status', 'reply_to_ticket'],
  },
];

export function getDefaultRosterAgent(agentId) {
  return DEFAULT_AI_ROSTER_AGENTS.find((a) => a.id === agentId) || null;
}

/**
 * Deterministic Firestore doc id for a company's CSA agent.
 * @param {string} companyId
 * @returns {string}
 */
export function customerSuccessDocId(companyId) {
  const safe = String(companyId || '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 80);
  return `csa_${safe || 'unknown'}`;
}
